import { useState, useCallback, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Upload, Check, Loader2, ExternalLink, AlertCircle } from 'lucide-react'
import { useDropzone } from 'react-dropzone'
import { seoAnalytics } from '@/lib/api'

interface GscConfigModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function GscConfigModal({ isOpen, onClose }: GscConfigModalProps) {
  const [step, setStep] = useState<'credentials' | 'oauth' | 'site'>('credentials')
  const [credentialsJson, setCredentialsJson] = useState<Record<string, any> | null>(null)
  const [oauthUrl, setOauthUrl] = useState<string | null>(null)
  const [selectedSite, setSelectedSite] = useState<string>('')
  const queryClient = useQueryClient()

  // Get current status
  const statusQuery = useQuery({
    queryKey: ['gsc-status'],
    queryFn: () => seoAnalytics.getGscStatus(),
    enabled: isOpen,
  })

  // Get sites when authenticated
  const sitesQuery = useQuery({
    queryKey: ['gsc-sites'],
    queryFn: () => seoAnalytics.getGscSites(),
    enabled: step === 'site' && statusQuery.data?.data?.has_token,
  })

  const status = statusQuery.data?.data

  // Determine initial step based on status
  useEffect(() => {
    if (status) {
      if (status.configured) {
        setStep('site')
      } else if (status.has_token) {
        setStep('site')
      } else if (status.has_credentials) {
        setStep('oauth')
      } else {
        setStep('credentials')
      }
    }
  }, [status])

  // Upload credentials mutation
  const uploadCredsMutation = useMutation({
    mutationFn: (creds: Record<string, any>) => seoAnalytics.setGscCredentials(creds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gsc-status'] })
      setStep('oauth')
    },
  })

  // Get OAuth URL mutation
  const getOAuthUrlMutation = useMutation({
    mutationFn: () => {
      const redirectUri = `${window.location.origin}/seo-analytics/gsc-callback`
      return seoAnalytics.getGscOAuthUrl(redirectUri)
    },
    onSuccess: (data) => {
      setOauthUrl(data.auth_url)
    },
  })

  // Set site mutation
  const setSiteMutation = useMutation({
    mutationFn: (siteUrl: string) => seoAnalytics.setGscSite(siteUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gsc-status'] })
      onClose()
    },
  })

  // Handle file drop for credentials JSON
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = () => {
        try {
          const json = JSON.parse(reader.result as string)
          setCredentialsJson(json)
        } catch {
          alert('Invalid JSON file')
        }
      }
      reader.readAsText(file)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/json': ['.json'],
    },
    maxFiles: 1,
  })

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
            Configurar Google Search Console
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 mb-6">
          <div className={`flex items-center gap-1.5 ${step === 'credentials' ? 'text-primary-600' : 'text-slate-400'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${
              step === 'credentials' ? 'bg-primary-100 text-primary-600' :
              status?.has_credentials ? 'bg-green-100 text-green-600' : 'bg-slate-100'
            }`}>
              {status?.has_credentials ? <Check className="w-4 h-4" /> : '1'}
            </div>
            <span className="text-sm">Credenciales</span>
          </div>
          <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
          <div className={`flex items-center gap-1.5 ${step === 'oauth' ? 'text-primary-600' : 'text-slate-400'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${
              step === 'oauth' ? 'bg-primary-100 text-primary-600' :
              status?.has_token ? 'bg-green-100 text-green-600' : 'bg-slate-100'
            }`}>
              {status?.has_token ? <Check className="w-4 h-4" /> : '2'}
            </div>
            <span className="text-sm">OAuth</span>
          </div>
          <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
          <div className={`flex items-center gap-1.5 ${step === 'site' ? 'text-primary-600' : 'text-slate-400'}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${
              step === 'site' ? 'bg-primary-100 text-primary-600' :
              status?.site_url ? 'bg-green-100 text-green-600' : 'bg-slate-100'
            }`}>
              {status?.site_url ? <Check className="w-4 h-4" /> : '3'}
            </div>
            <span className="text-sm">Sitio</span>
          </div>
        </div>

        {/* Step content */}
        {step === 'credentials' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Sube el archivo JSON de credenciales OAuth de Google Cloud Console.
            </p>

            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-slate-300 dark:border-slate-600 hover:border-primary-400'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              {credentialsJson ? (
                <p className="text-sm text-green-600">
                  <Check className="w-4 h-4 inline mr-1" />
                  Archivo cargado: {credentialsJson.web?.client_id?.substring(0, 20)}...
                </p>
              ) : (
                <p className="text-sm text-slate-500">
                  Arrastra el archivo JSON o haz clic para seleccionar
                </p>
              )}
            </div>

            <a
              href="https://console.cloud.google.com/apis/credentials"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
            >
              <ExternalLink className="w-3 h-3" />
              Abrir Google Cloud Console
            </a>

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="btn btn-secondary">
                Cancelar
              </button>
              <button
                onClick={() => credentialsJson && uploadCredsMutation.mutate(credentialsJson.web || credentialsJson)}
                disabled={!credentialsJson || uploadCredsMutation.isPending}
                className="btn btn-primary"
              >
                {uploadCredsMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Siguiente'
                )}
              </button>
            </div>
          </div>
        )}

        {step === 'oauth' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Autoriza el acceso a Google Search Console.
            </p>

            {oauthUrl ? (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                  Haz clic en el boton para autorizar en Google. Despues de autorizar,
                  copia el codigo y pegalo aqui.
                </p>
                <a
                  href={oauthUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary w-full"
                >
                  <ExternalLink className="w-4 h-4" />
                  Autorizar en Google
                </a>
              </div>
            ) : (
              <button
                onClick={() => getOAuthUrlMutation.mutate()}
                disabled={getOAuthUrlMutation.isPending}
                className="btn btn-primary w-full"
              >
                {getOAuthUrlMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Obtener enlace de autorizacion'
                )}
              </button>
            )}

            <div className="flex justify-between">
              <button onClick={() => setStep('credentials')} className="btn btn-secondary">
                Atras
              </button>
              <button
                onClick={() => {
                  queryClient.invalidateQueries({ queryKey: ['gsc-status'] })
                  setStep('site')
                }}
                className="btn btn-secondary"
              >
                Ya autorice
              </button>
            </div>
          </div>
        )}

        {step === 'site' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Selecciona el sitio web de Google Search Console.
            </p>

            {status?.has_token ? (
              <>
                {sitesQuery.isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : sitesQuery.data?.data?.length ? (
                  <select
                    value={selectedSite}
                    onChange={(e) => setSelectedSite(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700"
                  >
                    <option value="">Selecciona un sitio...</option>
                    {sitesQuery.data.data.map((site: string) => (
                      <option key={site} value={site}>
                        {site}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        No se encontraron sitios verificados.
                      </p>
                      <a
                        href="https://search.google.com/search-console"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-amber-600 hover:underline inline-flex items-center gap-1 mt-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Verificar sitio en Search Console
                      </a>
                    </div>
                  </div>
                )}

                <div className="flex justify-between">
                  <button onClick={() => setStep('oauth')} className="btn btn-secondary">
                    Atras
                  </button>
                  <button
                    onClick={() => selectedSite && setSiteMutation.mutate(selectedSite)}
                    disabled={!selectedSite || setSiteMutation.isPending}
                    className="btn btn-primary"
                  >
                    {setSiteMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Guardar'
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Primero completa la autorizacion OAuth.
                </p>
                <button onClick={() => setStep('oauth')} className="btn btn-secondary mt-2">
                  Ir a OAuth
                </button>
              </div>
            )}
          </div>
        )}

        {/* Status info */}
        {status?.configured && status.site_url && (
          <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2 text-sm text-green-600">
              <Check className="w-4 h-4" />
              <span>Configurado: {status.site_url}</span>
            </div>
            {status.last_sync && (
              <p className="text-xs text-slate-500 mt-1">
                Ultima sync: {new Date(status.last_sync).toLocaleString('es-ES')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
